import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { BiTrash } from "react-icons/bi";
import { FiExternalLink } from "react-icons/fi";
import ReactJson from "react-json-view";
import Confirm from "../../../../components/confirm/Confirm";
import Select from "../../../../components/select/Select";
import { IntegrationActionType, IntegrationType } from "../../../../__generated__/api";
import WorkflowServiceActions from "./Actions";
import WorkflowServiceApis from "./Apis";
import WorkflowServiceAuth from "./Authentication";
import { createId, Json } from "./base";
import Styles from "./CreateService.module.scss";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import WorkflowServiceOptions from "./Options";
import WorkflowServiceArticles from "./Articles";
import WorkflowServiceApiSelector from "./ApiSelector";
import WorkflowServiceUrl from "./AuthenticationUrl";
import WorkflowServiceAnswers from "./Answers";
import WorkflowServiceApiKey from "./AuthenticationApiKey";
import WorkflowServiceManual from "./AuthenticationManual";
import WorkflowServiceToken from "./AuthenticationToken";

import TagList from "../../../../components/tag-list/TagList";
import BooleanSelect from "../../../../components/pickers/boolean/BooleanSelect";

const ACTION_TYPES = Object.entries(IntegrationActionType).map(([label, value]) => ({
    label,
    value: value as string,
}));

const LOCAL_STORAGE_KEY = "admin.workflows";
export default function WorkflowServiceCreate(): ReactElement {
    const [json, setJson] = useState<Record<string, unknown | Json>>(Object.create(null));
    const [allWorkflows, setAllWorkflows] = useState<Json>(
        () => JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? "{}") ?? {},
    );
    const [showImport, setShowImport] = useState(false);
    const [importText, setImportText] = useState("");

    const [editing, setEditing] = useState<Json>();

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(allWorkflows));
    }, [allWorkflows]);

    useEffect(() => {
        if (json.id) {
            setAllWorkflows({
                ...allWorkflows,
                [json.id as string]: json,
            });
        }
    }, [json]);

    const connection = json.connection as Record<string, string>;

    const selectedTypes = new Set(json.actionTypes as string[]);
    const selectedActionTypes = ACTION_TYPES.filter(row => selectedTypes.has(row.value));

    return (
        <div className="border p-4 rounded mb-5 bg-white h-100 d-flex">

            <div className="col me-2 pe-2 border-end">

                <ExpandingSection title="Basic info" defaultExpanded>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">ID</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="ID"
                            required
                            onChange={evt => {
                                const oldId = json.id as string;
                                setAllWorkflows(current => {
                                    const copy = { ...current };
                                    delete copy[oldId];
                                    return copy;
                                });
                                const id = evt.target.value;
                                setJson(curr => ({ ...curr, id }));
                            }}
                            value={json.id as string ?? ""}
                        />
                        <div>
                            <button
                                type="button"
                                className="btn btn-none btn-sm"
                                onClick={() => setJson(curr => ({ ...curr, id: createId() }))}
                            >
                                Generate Id
                            </button>
                        </div>

                        <Form.Text>
                            The name of the service
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Name"
                            required
                            onChange={evt => setJson(curr => ({ ...curr, name: evt.target.value }))}
                            value={json.name as string ?? ""}
                        />
                        <Form.Text>
                            The name of the service
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Icon URL</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="https://"
                            required
                            onChange={evt => setJson(curr => ({ ...curr, iconUrl: evt.target.value }))}
                            value={json.iconUrl as string ?? ""}
                        />
                        <Form.Text>
                            The main icon users will see.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Short Description</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Short Description"
                            required
                            onChange={evt => setJson(curr => ({ ...curr, shortDescription: evt.target.value }))}
                            value={json.shortDescription as string ?? ""}
                        />
                        <Form.Text>
                            A short description that shows up on the main page
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Long Description</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Long Description"
                            required
                            as="textarea"
                            onChange={evt => setJson(curr => ({ ...curr, longDescription: evt.target.value }))}
                            value={json.longDescription as string ?? ""}
                        />
                        <Form.Text>
                            A long description that shows up when you click it open.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Tags</Form.Label>
                        <TagList
                            isTag
                            shouldSort
                            onChange={values => setJson(curr => ({ ...curr, tags: values }))}
                            value={json.tags as string[] ?? []}
                        />
                        <Form.Text>
                            The tags categorizing this integration
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Should Synchronize Public</Form.Label>
                        <BooleanSelect
                            onChange={value => setJson(curr => ({ ...curr, shouldSynchronizePublic: value }))}
                            value={json.shouldSynchronizePublic as boolean ?? false}
                        />
                        <Form.Text>
                            Should this integration be visible publically on the web.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Integration Type</Form.Label>
                        <Select
                            placeholder="Integration type"
                            options={Object.entries(IntegrationType).map(([label, value]) => ({
                                label,
                                value: value as string,
                            }))}
                            isClearable
                            onChange={type => setJson(curr => ({ ...curr, integrationType: type?.value }))}
                            value={!json.integrationType ? undefined : ({
                                label: Object
                                    .entries(IntegrationType)
                                    .find(([, value]) => value === json.integrationType)?.firstElement as string,
                                value: json.integrationType as string,
                            })}
                        />
                        <Form.Text>
                            The type of integration or none.
                        </Form.Text>

                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Action Types</Form.Label>
                        <Select
                            placeholder="Action type"
                            options={Object.entries(IntegrationActionType).map(([label, value]) => ({
                                label,
                                value: value as string,
                            }))}
                            isClearable
                            isMulti
                            onChange={types => setJson(curr => ({ ...curr, actionTypes: types.map(row => row.value) }))}
                            value={selectedActionTypes}
                        />
                        <Form.Text>
                            Action Types
                        </Form.Text>

                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Published Date</Form.Label>
                        <Form.Control
                            type="date"
                            placeholder="1/10/2020"
                            required
                            onChange={evt => setJson(curr => {
                                const copy = { ...curr };
                                if (evt.target.value) {
                                    copy.publishedDate = evt.target.value;
                                } else {
                                    delete copy.publishedDate;
                                }
                                return copy;
                            })}
                            value={json.publishedDate as string ?? ""}
                        />
                        <Form.Text>
                            When this is published. Will not show up if not published
                        </Form.Text>
                    </Form.Group>

                    <ExpandingSection title="Learn more">
                        <>
                            {[
                                {
                                    key: "websiteUrl",
                                    title: "Website Url",
                                    description: "The main page for the partner",
                                },
                                {
                                    key: "termsOfServiceUrl",
                                    title: "Terms of Service Url",
                                    description: "The terms of service url",
                                },
                                {
                                    key: "privacyPolicyUrl",
                                    title: "Privacy Policy Url",
                                    description: "The privacy policy url",
                                },
                                {
                                    key: "dataProtectionUrl",
                                    title: "Data Protection Url",
                                    description: "The privacy policy url",
                                },
                                {
                                    key: "securityCenterUrl",
                                    title: "Security Center Url",
                                    description: "Security Center URL (maybe security whitepaper or SOC2 attestation)",
                                },
                                {
                                    key: "supportCenterUrl",
                                    title: "Support Center Url",
                                    description: "Where to reach out for support",
                                },
                            ].map(({ key, title, description }) => (
                                <Form.Group className="mb-3" key={key}>
                                    <Form.Label className="fw-bolder">{title}</Form.Label>
                                    <Form.Control
                                        type="url"
                                        placeholder="https://"
                                        required
                                        onChange={evt => setJson(curr => ({
                                            ...curr,
                                            learnMore: {
                                                ...(json.learnMore as Json ?? {}),
                                                [key]: evt.target.value,
                                            },
                                        }))}
                                        value={(json.learnMore as Json)?.[key] as string ?? ""}
                                    />
                                    <Form.Text>
                                        {description}
                                    </Form.Text>
                                </Form.Group>
                            ))}
                        </>

                    </ExpandingSection>

                </ExpandingSection>

                <ExpandingSection title="Connection">
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Name"
                            required
                            onChange={evt => setJson(curr => ({
                                ...curr,
                                connection: {
                                    ...connection,
                                    name: evt.target.value,
                                },
                            }))}
                            value={connection?.name as string ?? ""}
                        />
                        <Form.Text>
                            The name of the connection
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Button Text</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Name"
                            required
                            onChange={evt => setJson(curr => ({
                                ...curr,
                                connection: {
                                    ...connection,
                                    buttonText: evt.target.value,
                                },
                            }))}
                            value={connection?.buttonText as string ?? ""}
                        />
                        <Form.Text>
                            The name of the connection
                        </Form.Text>
                    </Form.Group>

                    <WorkflowServiceAuth json={json} onChange={setJson} rootJson={json} />
                    <WorkflowServiceUrl json={json} onChange={setJson} rootJson={json} />
                    <WorkflowServiceApiKey json={json} onChange={setJson} rootJson={json} />
                    <WorkflowServiceManual json={json} onChange={setJson} rootJson={json} />
                    <WorkflowServiceToken json={json} onChange={setJson} rootJson={json} />

                </ExpandingSection>
                <ExpandingSection title="Options">
                    <WorkflowServiceOptions
                        rootJson={json}
                        json={json.options as Json[]}
                        onChange={options => setJson(curr => ({ ...curr, options }))}
                    />
                </ExpandingSection>
                <ExpandingSection title="Apis">
                    <WorkflowServiceApis
                        rootJson={json}
                        json={json.apis as Json[]}
                        onChange={apis => setJson(curr => ({ ...curr, apis }))}
                    />
                </ExpandingSection>
                <ExpandingSection title="Actions">
                    <WorkflowServiceActions
                        rootJson={json}
                        json={json.actions as Json[]}
                        onChange={actions => setJson(curr => ({ ...curr, actions }))}

                    />
                </ExpandingSection>

                <ExpandingSection title="Services">

                    <WorkflowServiceArticles
                        rootJson={json}
                        json={json.articles as Json}
                        onChange={articles => setJson(curr => ({ ...curr, articles }))}
                    />

                    <WorkflowServiceAnswers
                        rootJson={json}
                        json={json.answers as Json}
                        onChange={answers => setJson(curr => ({ ...curr, answers }))}
                    />

                    <ExpandingSection title="User">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">User</Form.Label>
                            <Form.Text className="d-block">
                                User
                            </Form.Text>
                            <WorkflowServiceApiSelector
                                rootJson={json}
                                json={(json?.data as Json)?.user as Json}
                                onChange={user => setJson(curr => ({
                                    ...curr,
                                    data: {
                                        ...(curr.data as Json ?? {}),
                                        user,
                                    },
                                }))}

                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">Look up by email</Form.Label>
                            <Form.Text className="d-block">
                                Looks up a user by email
                            </Form.Text>
                            <WorkflowServiceApiSelector
                                rootJson={json}
                                json={(json?.users as Json)?.lookupByEmail as Json}
                                onChange={lookupByEmail => setJson(curr => ({
                                    ...curr,
                                    users: {
                                        ...(curr.users as Json ?? {}),
                                        lookupByEmail,
                                    },
                                }))}

                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">Get user</Form.Label>
                            <Form.Text className="d-block">
                                Endpoint to manage getting a user by id
                            </Form.Text>
                            <WorkflowServiceApiSelector
                                rootJson={json}
                                json={(json?.users as Json)?.getUser as Json}
                                onChange={getUser => setJson(curr => ({
                                    ...curr,
                                    users: {
                                        ...(curr.users as Json ?? {}),
                                        getUser,
                                    },
                                }))}

                            />
                        </Form.Group>

                    </ExpandingSection>

                </ExpandingSection>

            </div>

            <div className="col-4">
                <Confirm
                    shouldShow={showImport}
                    onHide={() => setShowImport(false)}
                    size="xl"
                    dialogClassName="full-size-container"
                    onConfirm={() => {
                        setJson(JSON.parse(importText));
                        setShowImport(false);
                    }}
                    confirmText="Import"
                >
                    <Form.Control
                        type="text"
                        placeholder={`{\n\t"id": "name"\n}`}
                        required
                        as="textarea"
                        className="h-100"
                        onChange={evt => setImportText(evt.target.value)}
                        value={importText}
                    />
                </Confirm>
                <Confirm
                    shouldShow={editing != null}
                    onHide={() => setEditing(undefined)}
                    size="xl"
                    dialogClassName="full-size-container"
                    onConfirm={() => {
                        setJson(editing as Json);
                        setEditing(undefined);
                    }}
                    confirmText="Save"
                >
                    <ReactJson
                        src={editing as object}
                        onEdit={value => setEditing(value.updated_src as Json)}
                        onDelete={value => setEditing(value.updated_src as Json)}
                        onAdd={value => setEditing(value.updated_src as Json)}
                        displayDataTypes={false}
                    />

                </Confirm>
                <ExpandingSection title="Current Workflows" defaultExpanded>
                    <ul className="list-group list-group-flush">

                        {Object.entries(allWorkflows).sortBy(curr => curr.firstElement).map(([id, value]) => {
                            const formatted = value as Record<string, string>;
                            const name = formatted.name ?? "[No Name]";
                            return (
                                <li
                                    key={id}
                                    className="list-group-item d-flex justify-content-between"
                                >
                                    <button
                                        type="button"
                                        className="w-100 btn btn-none p-0 text-start flex-grow-1"
                                        onClick={() => setJson(formatted)}
                                    >
                                        {name}

                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-none btn-sm p-0 text-light  "
                                        onClick={() => {
                                            // eslint-disable-next-line no-restricted-globals, no-alert
                                            if (confirm(`Are you sure you want to delete ${name}?`)) {
                                                setAllWorkflows(current => {
                                                    const copy = { ...current };
                                                    delete copy[id];
                                                    return copy;
                                                });
                                            }
                                        }}
                                    >
                                        <BiTrash />
                                    </button>

                                </li>
                            );
                        })}
                        {Object.keys(allWorkflows).isEmpty ? (
                            <small className="text-light text-center">No Workflows. Create the first one on the left.</small>
                        ) : (
                            <li className="list-group-item p-0 pt-2">
                                <button type="button" className="btn btn-primary py-1 px-2" onClick={() => setJson({})}>
                                    New
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary py-1 px-2 ms-3"
                                    onClick={() => setShowImport(true)}
                                >
                                    Import
                                </button>
                            </li>
                        )}

                    </ul>
                </ExpandingSection>

                <button
                    type="button"
                    className="btn btn-success mb-4"
                    onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(json, null, 4));
                    }}
                >
                    Copy Code
                </button>
                <ExpandingSection
                    title="Code"
                    className="p-0"
                    topRightButton={<FiExternalLink className="text-light" />}
                    topRightClicked={() => {
                        setEditing(json);
                    }}
                >
                    <div className={`alert alert-info overflow-auto ${Styles["code-block"]}`}>
                        <ReactJson
                            src={json as object}
                        />
                    </div>

                </ExpandingSection>
            </div>

        </div>

    );
}
